// ExprInput — поле размера, принимающее ВЫРАЖЕНИЕ, а не только число.
//
// Как в Fusion 360: в любое поле размера можно написать формулу со ссылками
// на параметры — `толщина * 3`, `sqrt(16) + 2`, `max(10; высота)`. Именно
// поэтому там модель легко перестраивается: размер связан с замыслом, а не
// вбит числом.
//
// 🔴 Почему не <input type="number">: в него браузер физически не даёт
// ввести буквы и скобки — формулу набрать невозможно. Поэтому здесь обычное
// текстовое поле, а число получается вычислением.
//
// Поведение:
//   • пока набирают — показываем ровно то, что ввели;
//   • под полем показываем вычисленное значение (как в Fusion);
//   • ошибку показываем сразу, но НЕ ломаем модель: наверх уходит только
//     корректное число;
//   • при потере фокуса некорректный ввод откатывается на прежнее значение.
import React, { useState, useEffect, useMemo } from 'react';
import useCadStore from '../store/useCadStore';
import { computeParameters, resolveValue } from '../parameters';
import cl from './Properties.module.css';

export default function ExprInput({
    value,            // число ИЛИ строка-выражение
    onChange,         // (число, исходнаяСтрока) => void
    unit = 'мм',
    min,
    placeholder,
    title,
    /*
     * Разрешить ПУСТОЕ значение. Нужно там, где «ничего не задано» — само
     * по себе осмысленный ответ: предел хода соединения, необязательный
     * параметр. Тогда очистка поля отдаёт наверх null, а не молча
     * игнорируется, и снять однажды заданный предел становится возможно.
     */
    allowEmpty = false,
    /*
     * Поставить фокус в поле сразу при появлении и выделить содержимое.
     *
     * Нужно там, где поле — ГЛАВНОЕ в форме и человек почти наверняка сразу
     * начнёт вводить число: расстояние выдавливания, радиус скругления.
     * Иначе после выбора грани приходится ещё раз целиться мышью в поле,
     * хотя следующий шаг очевиден. Значение выделяем, чтобы набор сразу
     * заменял старое, а не дописывался к нему.
     */
    autoFocus = false,
}) {
    const parameters = useCadStore((s) => s.doc.parameters);
    const inputRef = React.useRef(null);
    useEffect(() => {
        if (!autoFocus) return;
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.select();
    }, [autoFocus]);

    // Значения параметров считаем один раз на изменение их списка.
    const paramValues = useMemo(
        () => computeParameters(parameters || []).values,
        [parameters],
    );

    // Текст в поле: строка-выражение как есть, число — как число.
    const [text, setText] = useState(() => String(value ?? ''));
    const [focused, setFocused] = useState(false);

    // Пока поле не в фокусе — показываем то, что пришло снаружи
    // (значение мог поменять другой элемент интерфейса).
    useEffect(() => {
        if (!focused) setText(String(value ?? ''));
    }, [value, focused]);

    // Вычисляем текущий текст.
    const { num, error } = useMemo(() => {
        const t = String(text).trim();
        if (t === '') return { num: null, error: null };
        let v;
        try {
            v = resolveValue(t, paramValues);
        } catch (e) {
            return { num: null, error: 'Не удалось вычислить' };
        }
        if (typeof v !== 'number' || !isFinite(v)) {
            return { num: null, error: 'Неизвестное имя или ошибка в формуле' };
        }
        if (typeof min === 'number' && v < min) {
            return { num: v, error: `Меньше допустимого (${min})` };
        }
        return { num: v, error: null };
    }, [text, paramValues, min]);

    // Формула ли это (не просто число) — тогда показываем результат под полем.
    const isFormula = useMemo(() => {
        const t = String(text).trim();
        return t !== '' && !/^-?\d*\.?\d+$/.test(t);
    }, [text]);

    const commit = (t) => {
        const s = String(t).trim();
        if (s === '') return;
        let v;
        try { v = resolveValue(s, paramValues); } catch { return; }
        if (typeof v !== 'number' || !isFinite(v)) return;
        if (typeof min === 'number' && v < min) return;
        // Наверх отдаём и число (для построения), и исходную строку —
        // чтобы связь с параметром не потерялась при пересчёте модели.
        onChange(v, s);
    };

    return (
        <div>
            <div
                className={cl.inputWithUnit}
                style={error ? { borderColor: '#d05050' } : undefined}
            >
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    className={cl.numInput}
                    value={text}
                    placeholder={placeholder}
                    title={title || 'Можно вводить формулу: толщина * 3'}
                    onFocus={() => setFocused(true)}
                    onChange={(e) => {
                        setText(e.target.value);
                        // Считаем на лету: модель перестраивается сразу,
                        // как в Fusion, но только при корректном значении.
                        const s = e.target.value.trim();
                        if (s === '') { if (allowEmpty) onChange(null, ''); return; }
                        let v;
                        try { v = resolveValue(s, paramValues); } catch { return; }
                        if (typeof v === 'number' && isFinite(v)
                            && !(typeof min === 'number' && v < min)) {
                            onChange(v, s);
                        }
                    }}
                    onBlur={(e) => {
                        setFocused(false);
                        // Некорректный ввод откатываем — модель не должна
                        // остаться с мусором в поле.
                        if (String(e.target.value).trim() === '' && allowEmpty) {
                            onChange(null, '');
                        } else if (error || String(e.target.value).trim() === '') {
                            setText(String(value ?? ''));
                        } else {
                            commit(e.target.value);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.currentTarget.blur(); }
                        if (e.key === 'Escape') {
                            setText(String(value ?? ''));
                            e.currentTarget.blur();
                        }
                    }}
                />
                <span className={cl.unit}>{unit}</span>
            </div>
            {/* Под полем — результат формулы или причина ошибки. */}
            {error ? (
                <div style={{ fontSize: 11, color: '#e07a7a', marginTop: 3 }}>{error}</div>
            ) : isFormula && num !== null ? (
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>
                    = {Math.round(num * 1000) / 1000} {unit}
                </div>
            ) : null}
        </div>
    );
}
